import { Grid } from '@material-ui/core';
import {
  Button,
  Checkbox,
  Dialog,
  FormControlLabel,
  TextField,
  Typography,
} from '@nutrien/cxp-components';
import mime from 'mime-db';
import React, { useState } from 'react';

import { useNotification } from '../../utilities';
import { SPECIAL_CHARACTER_ERROR } from '../../utilities/constants';
import {
  getFileExtension,
  getFileName,
  hasSpecialCharacter,
} from '../../utilities/utilityFunctions';
import { useStyles } from '../Thumbnails/Thumbnail.styles';

interface Props {
  handleEditDetailsClose: () => void;
  openEditDetails: boolean;
  isShowOnReport: boolean;
  fileData: {
    fileName: string;
    fileDescription: string;
    fileShowOnReport: boolean;
    originalFileName: string;
    extension: string;
  };
  updateEditDetails: (payload: {
    fileName: string;
    fileDescription: string;
    fileShowOnReport: boolean;
  }) => void;
}

const EditFileDetails: React.FC<Props> = ({
  handleEditDetailsClose,
  openEditDetails,
  isShowOnReport,
  fileData,
  updateEditDetails,
}: Props) => {
  const classes = useStyles();
  const [editedFileName, setEditedFileName] = useState<string>(fileData.fileName);
  const { errorNotification } = useNotification();
  const [editedFileDescription, setEditedFileDescription] = useState<string>(
    fileData.fileDescription,
  );
  const [editedFileShowOnReport, setEditedFileShowOnReport] = useState<boolean>(
    fileData.fileShowOnReport,
  );
  const [disabledConfirmButton, setDisabledConfirmButton] = useState(false);

  const handleOnSubmit = () => {
    const payload = {
      fileName: editedFileName,
      fileDescription: editedFileDescription,
      fileShowOnReport: editedFileShowOnReport,
    };
    updateEditDetails(payload);
  };

  const handleOnFormSubmit = (event: React.KeyboardEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleOnSubmit();
  };

  return (
    <>
      <Dialog
        onClose={handleEditDetailsClose}
        open={openEditDetails}
        titleText="File Details"
        backButton={false}
        dialogActions={
          <Grid
            container
            justifyContent="flex-end"
            alignItems="center"
            className={classes.dialogPadding}
          >
            <Grid item>
              <Button
                disabled={disabledConfirmButton}
                noMinHeight
                color="primary"
                variant="contained"
                onClick={handleOnSubmit}
              >
                Confirm
              </Button>
            </Grid>
          </Grid>
        }
        maxWidth="xs"
        noMinHeight
      >
        <form onSubmit={handleOnFormSubmit}>
          <Grid container className={classes.dialogContentPadding}>
            <Grid item container xs={12} alignItems="center">
              <Typography className="fileName">File name</Typography>
              <TextField
                onChange={e => {
                  const fileName = getFileName(e.target.value);
                  let ext = getFileExtension(fileData.originalFileName);
                  if (ext === undefined) {
                    ext =
                      mime[fileData.extension]?.extensions &&
                      mime[fileData.extension].extensions[0];
                  }
                  const isValidFileName = hasSpecialCharacter(fileName || '');
                  if (!fileName || isValidFileName) {
                    setEditedFileName(fileData.fileName);
                    setDisabledConfirmButton(true);
                    if (isValidFileName) {
                      errorNotification(SPECIAL_CHARACTER_ERROR);
                      return;
                    }
                  } else {
                    setEditedFileName(fileName + '.' + ext);
                    setDisabledConfirmButton(false);
                  }
                }}
                required
                noMinHeight
                variant="outlined"
                size="small"
                defaultValue={editedFileName}
                className="inputName"
                inputProps={{
                  'data-testid': 'edit-file-name',
                }}
              />
              <TextField
                onChange={e => {
                  setEditedFileDescription(e.target.value);
                }}
                defaultValue={editedFileDescription}
                label="File description"
                noMinHeight
                variant="outlined"
                size="small"
                placeholder="Type File Description"
                multiline
                rows={2}
                rowsMax={4}
                className="textDescription"
                inputProps={{
                  'data-testid': 'edit-file-description',
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={isShowOnReport}
                    onChange={e => setEditedFileShowOnReport(e.target.checked)}
                    checked={editedFileShowOnReport}
                    data-testid="show-on-report"
                  />
                }
                label="Show on report"
              />
            </Grid>
          </Grid>
        </form>
      </Dialog>
    </>
  );
};

export default EditFileDetails;
